import { Award, Loader2, Monitor, X } from 'lucide-react';
import type { Presence } from 'shared';
import { useEquipments } from '@/hooks/useEquipments';
import { describeUnits, equipmentChoices } from '@/lib/presenceView';

interface EquipmentPickerModalProps {
  presence: Presence;
  /** The equipment being handed over right now, if any. */
  pendingId: number | null;
  onPick: (equipmentId: number, name: string) => void;
  onClose: () => void;
}

/** The centre's equipment, to hand one to a participant who is in it. */
export default function EquipmentPickerModal({
  presence,
  pendingId,
  onPick,
  onClose,
}: EquipmentPickerModalProps) {
  const { data, isLoading, isError } = useEquipments({
    digitalCenterID: presence.digitalCenterId,
    limit: 100,
  });
  const choices = equipmentChoices(data?.equipments ?? [], presence);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="equipment-picker-title"
    >
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div>
            <h2 id="equipment-picker-title" className="text-lg font-semibold text-foreground">
              Entregar equipamento
            </h2>
            <p className="text-sm text-muted-foreground">a {presence.participantName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-accent"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto">
          {isLoading && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
              <Loader2 className="w-4 h-4 animate-spin" />A carregar o equipamento…
            </p>
          )}
          {isError && (
            <p className="text-sm text-destructive" role="alert">
              Não foi possível carregar o equipamento do centro.
            </p>
          )}
          {!isLoading && !isError && choices.length === 0 && (
            <p className="text-sm text-muted-foreground">Este centro não tem equipamento registado.</p>
          )}

          {choices.length > 0 && (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {choices.map(({ equipment, free, blocked }) => (
                <li key={equipment.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                  <div className="min-w-0 flex items-center gap-3">
                    <Monitor className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {equipment.equipamento}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {blocked ?? describeUnits(free, equipment.quantidade)}
                        {equipment.badgeName && (
                          <span className="ml-2 inline-flex items-center gap-1">
                            <Award className="w-3 h-3" />
                            Exige {equipment.badgeName}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onPick(equipment.id, equipment.equipamento)}
                    disabled={blocked !== null || pendingId !== null}
                    aria-label={`Entregar ${equipment.equipamento}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
                  >
                    {pendingId === equipment.id && <Loader2 className="w-3 h-3 animate-spin" />}
                    Entregar
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
